<template>
  <div id="deductions-list-modal-app">
    <modal
      elem-id="modal_list_deductions"
      size="compactDesktop"
      :close-label="t('Fechar')"
      :title="modalTitle"
      :show="isVisible"
      @close="closeModal"
      @dismiss="closeModal"
    >
      <div v-if="isLoading" class="loading-container">
        <ProgressSpinner />
      </div>

      <div v-else-if="!modalData.userId" class="no-user-message">
        <Message severity="warn" :closable="false">
          {{ t('Nenhum colaborador selecionado') }}
        </Message>
      </div>

      <div v-else-if="deductions.length === 0" class="no-deductions-message">
        <div class="empty-state-icon">
          <i class="pi pi-inbox"></i>
        </div>
        <h4>{{ t('Nenhuma dedução encontrada') }}</h4>
        <p>{{ t('Este colaborador não possui deduções cadastradas') }}</p>
      </div>

      <div v-else>
        <!-- Summary Section -->
        <div class="deductions-summary">
          <div class="summary-item">
            <i class="pi pi-list"></i>
            <span class="summary-label">{{ t('Total de Deduções') }}:</span>
            <span class="summary-value">{{ deductions.length }}</span>
          </div>
          <div class="summary-item highlight">
            <i class="pi pi-dollar"></i>
            <span class="summary-label">{{ t('Valor Total') }}:</span>
            <span class="summary-value amount-total">{{ n(totalAmount || 0, 'currency') }}</span>
          </div>
        </div>

        <!-- Deductions List -->
        <div class="deductions-detail-list">
          <div
            v-for="(deduction, index) in deductions"
            :key="deduction.id"
            :data-testid="'deductions-row-' + deduction.id"
            class="deduction-detail-item"
          >
            <div class="deduction-item-number">{{ index + 1 }}</div>
            <div class="deduction-item-content">
              <div class="deduction-detail-header">
                <div class="amount-section">
                  <i class="pi pi-minus-circle amount-icon"></i>
                  <span class="deduction-detail-amount">{{ n(parseFloat(String(deduction.amount)) || 0, 'currency') }}</span>
                </div>
                <Button
                  data-testid="deductions-delete-button"
                  icon="pi pi-trash"
                  :label="t('Excluir')"
                  class="p-button-sm p-button-danger p-button-outlined"
                  @click.stop="handleDeleteDeduction(deduction.id, $event)"
                />
              </div>
              <div class="deduction-detail-description" v-html="sanitizeHtmlContent(deduction.description)"></div>
              <div class="deduction-detail-meta">
                <div class="meta-item">
                  <i class="pi pi-calendar"></i>
                  <span>{{ t('Criado em') }}: {{ formatDate(deduction.created_at) }}</span>
                </div>
                <div v-if="deduction.despesa_id" class="meta-item">
                  <i class="pi pi-link"></i>
                  <span>{{ t('Vinculado à despesa') }} #{{ deduction.despesa_id }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { sanitizeHtmlContent } from '@/utils/sanitizeHtmlContent';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';
import { useUserDeductions } from '@/composables/useUserDeductions';
import { useDateFormatter } from '@/composables/useDateFormatter';
import { useConfirmAction } from '@/composables/useConfirmAction';

interface Deduction {
  id: number | string;
  user_id: string;
  amount: number | string;
  description: string;
  despesa_id?: number | null;
  created_at?: string | null;
}

interface ModalData {
  periodId: number | null;
  userId: string | null;
  userName: string | null;
  franchiseId: number | null;
}

const { t, n } = useI18n();
const { listDeductionsByPeriod, deleteDeduction } = useUserDeductions();
const { confirmAction } = useConfirmAction();

const { formatDate: formatDateLocale } = useDateFormatter();

const isVisible = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const deductions = ref<Deduction[]>([]);
const modalData = ref<ModalData>({
  periodId: null,
  userId: null,
  userName: null,
  franchiseId: null
});

const modalTitle = computed<string>(() => {
  if (modalData.value.userName) {
    return t('Deduções de') + ' ' + modalData.value.userName;
  }
  return t('Deduções');
});

const totalAmount = computed<number>(() => {
  return deductions.value.reduce((sum, deduction) => sum + (parseFloat(String(deduction.amount)) || 0), 0);
});

const openModal = async (data: ModalData): Promise<void> => {
  log('DeductionsListModal', 'openModal', 'Opening modal', data);

  if (isVisible.value) {
    log('DeductionsListModal', 'openModal', 'Modal already visible, ignoring event');
    return;
  }

  modalData.value = {
    periodId: data.periodId,
    userId: data.userId,
    userName: data.userName,
    franchiseId: data.franchiseId
  };

  isVisible.value = true;

  await loadDeductions();
};

const closeModal = (): void => {
  log('DeductionsListModal', 'closeModal', 'Closing modal');
  isVisible.value = false;
  deductions.value = [];
  modalData.value = {
    periodId: null,
    userId: null,
    userName: null,
    franchiseId: null
  };
};

const loadDeductions = async (): Promise<void> => {
  if (!modalData.value.periodId || !modalData.value.userId) {
    return;
  }

  isLoading.value = true;
  try {
    log('DeductionsListModal', 'loadDeductions', 'Loading deductions', {
      periodId: modalData.value.periodId,
      userId: modalData.value.userId
    });

    const allDeductions = await listDeductionsByPeriod(modalData.value.periodId) as Deduction[];

    deductions.value = allDeductions.filter(d => d.user_id === modalData.value.userId);

    log('DeductionsListModal', 'loadDeductions', 'Deductions loaded', deductions.value);
  } catch (error) {
    const message = error instanceof Error ? error.message : t('Erro ao carregar deduções');
    console.error('Error loading deductions:', error);
    eventBus.emit('globalMessage.error', {
      message,
      duration: 5000
    });
  } finally {
    isLoading.value = false;
  }
};

const handleDeleteDeduction = (deductionId: number | string, event?: Event): void => {
  if (event) {
    event.stopPropagation();
    event.preventDefault();
  }

  const deductionIdStr = String(deductionId);

  const deductionExists = deductions.value.some(d => String(d.id) === deductionIdStr);
  if (!deductionExists) {
    log('DeductionsListModal', 'handleDeleteDeduction', 'Deduction not found in list, ignoring', { deductionId });
    return;
  }

  confirmAction({
    message: t('Tem certeza que deseja deletar esta dedução?'),
    header: t('Confirmação'),
    onAccept: async () => {
      const stillExists = deductions.value.some(d => String(d.id) === deductionIdStr);
      if (!stillExists) {
        log('DeductionsListModal', 'handleDeleteDeduction', 'Deduction no longer exists, ignoring accept');
        return;
      }

      try {
        log('DeductionsListModal', 'handleDeleteDeduction', 'Proceeding with deletion', { deductionId });
        const success = await deleteDeduction(deductionId, modalData.value.franchiseId);

        if (success) {
          eventBus.emit('globalMessage.success', {
            message: t('Dedução deletada com sucesso'),
            duration: 3000
          });

          await loadDeductions();

          eventBus.emit('deduction.deleted', {
            periodId: modalData.value.periodId,
            userId: modalData.value.userId
          });
        } else {
          eventBus.emit('globalMessage.error', {
            message: t('Erro ao deletar dedução'),
            duration: 5000
          });
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : t('Erro ao deletar dedução');
        console.error('Error deleting deduction:', error);
        if (!message.includes('não encontrada') && !message.includes('not found')) {
          eventBus.emit('globalMessage.error', {
            message,
            duration: 5000
          });
        }
      }
    }
  });
};

const formatDate = (dateString?: string | null): string => {
  if (!dateString) return '';
  return formatDateLocale(new Date(dateString));
};

const handleOpenList = (data?: ModalData): void => {
  if (!data) return;
  void openModal(data);
};

const handleDeductionSaved = (): void => {
  void loadDeductions();
};

onMounted(() => {
  log('DeductionsListModal', 'onMounted', 'Component mounted, setting up event listeners');
  eventBus.on('deduction.open-list-modal', handleOpenList);
  eventBus.on('deduction.saved', handleDeductionSaved);
});

onBeforeUnmount(() => {
  log('DeductionsListModal', 'onBeforeUnmount', 'Component unmounting, cleaning up event listeners');
  eventBus.off('deduction.open-list-modal', handleOpenList);
  eventBus.off('deduction.saved', handleDeductionSaved);
});

defineExpose({
  openModal,
  closeModal
});
</script>

<style scoped>

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
}

.no-user-message {
  padding: 1rem;
}

.no-deductions-message {
  text-align: center;
  padding: 3rem 2rem;
  background: var(--surface-50);
  border-radius: 12px;
  border: 2px dashed var(--surface-300);
}

.empty-state-icon {
  font-size: 4rem;
  color: var(--text-color-secondary);
  margin-bottom: 1.5rem;
  opacity: 0.5;
}

.no-deductions-message h4 {
  margin: 0 0 0.5rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-color);
}

.no-deductions-message p {
  margin: 0;
  color: var(--text-color-secondary);
  font-size: 0.95rem;
}

/* Summary Section */
.deductions-summary {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: var(--surface-50);
  border-radius: 8px;
  border: 1px solid var(--surface-200);
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
}

.summary-item i {
  font-size: 1.25rem;
  color: var(--text-color-secondary);
}

.summary-item.highlight {
  padding-left: 1rem;
  border-left: 2px solid var(--surface-300);
}

.summary-label {
  font-weight: 500;
  color: var(--text-color-secondary);
  font-size: 0.9rem;
}

.summary-value {
  font-weight: 600;
  color: var(--text-color);
  font-size: 1rem;
}

.summary-value.amount-total {
  font-size: 1.25rem;
  color: var(--red-600);
}

/* Deductions List */
.deductions-detail-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.deduction-detail-item {
  display: flex;
  gap: 1rem;
  padding: 1.25rem;
  background: white;
  border-radius: 8px;
  border: 1px solid var(--surface-200);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  position: relative;
}

.deduction-detail-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: var(--surface-300);
  transform: translateY(-1px);
}

.deduction-item-number {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 2.5rem;
  height: 2.5rem;
  background: var(--primary-50);
  color: var(--primary-color);
  border-radius: 50%;
  font-weight: 700;
  font-size: 1rem;
  flex-shrink: 0;
}

.deduction-item-content {
  flex: 1;
  min-width: 0;
}

.deduction-detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  gap: 1rem;
}

.amount-section {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.amount-icon {
  font-size: 1.5rem;
  color: var(--red-500);
}

.deduction-detail-amount {
  color: var(--red-600);
  font-weight: 700;
  font-size: 1.5rem;
  line-height: 1.2;
}

.deduction-detail-description {
  font-weight: 400;
  margin-bottom: 0.75rem;
  word-wrap: break-word;
  color: var(--text-color);
  line-height: 1.6;
  font-size: 0.95rem;
}

.deduction-detail-description :deep(p) {
  margin: 0.5rem 0;
  line-height: 1.6;
}

.deduction-detail-description :deep(p:first-child) {
  margin-top: 0;
}

.deduction-detail-description :deep(p:last-child) {
  margin-bottom: 0;
}

.deduction-detail-description :deep(ul),
.deduction-detail-description :deep(ol) {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

.deduction-detail-description :deep(li) {
  margin: 0.25rem 0;
}

.deduction-detail-meta {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--surface-200);
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-color-secondary);
  font-size: 0.875rem;
}

.meta-item i {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
  opacity: 0.7;
}

/* Responsive Design */
@media (max-width: 768px) {
  .deductions-summary {
    flex-direction: column;
    gap: 0.75rem;
  }

  .summary-item.highlight {
    padding-left: 0;
    border-left: none;
    border-top: 2px solid var(--surface-300);
    padding-top: 0.75rem;
  }

  .deduction-detail-item {
    flex-direction: column;
    gap: 0.75rem;
  }

  .deduction-item-number {
    align-self: flex-start;
  }

  .deduction-detail-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .deduction-detail-amount {
    font-size: 1.25rem;
  }
}
</style>

